// permissions.ts — katalog uprawnień Control Center.
//
// ==========================================================================================
//  TWARDA ZASADA BEZPIECZEŃSTWA (R5-15 §21)
//  Ukrycie elementu w interfejsie NIE JEST autoryzacją.
//  Ten moduł steruje WYŁĄCZNIE tym, co użytkownik widzi. Jedyną autoryzacją, która się liczy,
//  jest kontrola po stronie backendu. Jeżeli kiedykolwiek pojawi się endpoint chroniony tylko
//  tym plikiem — to jest luka bezpieczeństwa, nie funkcja.
// ==========================================================================================
//
// Model jest zorientowany na UPRAWNIENIA, nie na role. Role są tylko wygodnym opakowaniem,
// bo backendy TuSzamy dziś operują rolami (crm_admin, notification_templates, admin, subadmin,
// DELIVER…). Mapowanie rola → uprawnienia jest w roleMap.ts i to ono jest tymczasowe.

export const PERMISSIONS = [
  'analytics.sales.read',
  'analytics.delivery.read',
  'admin.users.read',
  'admin.users.write',
  'restaurant.menu.read',
  'restaurant.menu.write',
  'restaurant.sales.read',
  // Rozliczenia tygodniowe restauracji (obrót, prowizja, wypłata) całej sieci. Tylko odczyt —
  // celowo NIE ma `settlements.write`: ekran niczego nie zapisuje. Backend wpuszcza wyłącznie
  // rolę `admin` (`bramaCrm` na `/api/cc/admin/rozliczenia`).
  'settlements.read',
  // Administracja → Grafik (kanoniczny grafik szkół i restauracji z systemu głównego). Zapis idzie przez
  // API systemu głównego; backend wpuszcza wyłącznie rolę `admin` (`bramaCrm` na `/api/cc/admin/grafik`).
  'schedule.read',
  'schedule.write',
  // ADMINISTRACJA (OWNER 2026-10-10) — narzędzia administratora przeniesione ze starego panelu admina.
  // Każda operacja idzie do systemu głównego tą samą operacją biznesową co stary panel; backend CC
  // wpuszcza wyłącznie rolę `admin` (`bramaCrm` na całym `/api/cc/admin/`), a system główny sprawdza
  // swoją rolę usługową CC per operacja (CC_USERS_READ, CC_WALLET_WRITE, …) i hasło operatora.
  'users.read', // Użytkownicy: lista kont, wyszukiwanie, filtry, saldo
  'users.wallet.write', // Użytkownicy: doładowanie portfela (hasło operatora panelu admina)
  'users.delete', // Użytkownicy: usunięcie konta klienta (hasło operatora panelu admina)
  'restaurantSchools.read', // Szkoły i restauracje: kto obsługuje którą szkołę
  'restaurantSchools.write', // Szkoły i restauracje: dodanie i odpięcie szkoły
  'menuModeration.read', // Moderacja menu: kolejka, historia, zatwierdzone przez AI
  'menuModeration.write', // Moderacja menu: zatwierdź / odrzuć
  'crm.campaigns.read',
  'crm.campaigns.write',
  'crm.customers.read',
  'drive.routes.read',
  'drive.deliveries.read',
  'reports.read',
  'system.incidents.read',
  'system.audit.read',
  /*
   * Treści aplikacji — skórki i żółty pasek. Trzy stopnie, bo to trzy różne decyzje:
   * podejrzeć, przygotować szkic, wypuścić do uczniów. Publikacja zmienia to, co widzą
   * WSZYSCY użytkownicy aplikacji, więc nie jest częścią „edycji".
   *
   * Zgodnie z zasadą wyżej: te uprawnienia sterują tylko widocznością w panelu. Publikację
   * egzekwuje system główny (klucz maszynowy na trasach `api/crm/message-bar`).
   */
  'app.content.read',
  'app.content.write',
  'app.content.publish',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export type PermissionSet = ReadonlySet<Permission>;

export function makePermissionSet(list: readonly Permission[]): PermissionSet {
  return new Set(list);
}

export const EMPTY_PERMISSIONS: PermissionSet = new Set<Permission>();

/** Czy zestaw uprawnień pokrywa WSZYSTKIE wymagane. Brak wymagań = dostęp otwarty. */
export function can(granted: PermissionSet, required: readonly Permission[] | undefined): boolean {
  if (!required || required.length === 0) return true;
  return required.every((p) => granted.has(p));
}

/** Czy zestaw pokrywa CHOĆ JEDNO z wymaganych (nawigacja: pokaż zakładkę, jeśli cokolwiek widać). */
export function canAny(granted: PermissionSet, required: readonly Permission[] | undefined): boolean {
  if (!required || required.length === 0) return true;
  return required.some((p) => granted.has(p));
}
